/**
 * Shared view sync interface for all Modifiers and Viewers.
 *
 * Allows syncing scroll position, active sheet, page, zoom, etc.
 * between an Editor's Modifier and Viewer (and between editor + version previewer).
 *
 * Data is stored as: viewSync[`${fileId}:${mode}`] = { scrollTop, activeSheet, ... }
 *
 * Each component reads/writes its own keys. The Editor container just passes
 * the ref through — it doesn't need to know the sync details.
 *
 * Standard keys (by convention):
 *   scrollTop   — vertical scroll position (text, grid)
 *   scrollLeft  — horizontal scroll position (text, grid)
 *   activeSheet — active sheet ID (spreadsheets)
 *   page        — current page number (PDF, presentations)
 *   zoom        — zoom level (images, PDF)
 */

/** Sync state for a single file+mode combination */
export type ViewSyncState = Record<string, unknown>

/** Map of all sync states, keyed by `${fileId}:${mode}` */
export type ViewSyncRef = Record<string, ViewSyncState>

/** Build the sync key for a file + mode */
export function viewSyncKey(fileId: string, mode: 'edit' | 'readonly'): string {
  return `${fileId}:${mode}`
}

/** Standard props for all Modifiers and Viewers */
export interface ViewSyncProps {
  /** Shared mutable sync state. Components read on mount, write on interaction. */
  viewSync?: ViewSyncRef
  /** Key into viewSync — usually `viewSyncKey(fileId, mode)` */
  viewSyncId?: string
  /** Increment to force re-read from viewSync (e.g., when conflict overlay opens/closes) */
  viewSyncTrigger?: number
}

/**
 * Read a sync value. Returns undefined if not set.
 */
export function readSync<T = unknown>(
  viewSync: ViewSyncRef | undefined,
  viewSyncId: string | undefined,
  key: string,
): T | undefined {
  if (!viewSync || !viewSyncId) return undefined
  return viewSync[viewSyncId]?.[key] as T | undefined
}

/**
 * Write a sync value.
 */
export function writeSync(
  viewSync: ViewSyncRef | undefined,
  viewSyncId: string | undefined,
  key: string,
  value: unknown,
): void {
  if (!viewSync || !viewSyncId) return
  if (!viewSync[viewSyncId]) viewSync[viewSyncId] = {}
  viewSync[viewSyncId][key] = value
}

/**
 * Copy sync state from one mode to another.
 * Used when opening conflict overlay (copy edit → readonly).
 */
export function copySyncState(
  viewSync: ViewSyncRef | undefined,
  fileId: string,
  fromMode: 'edit' | 'readonly',
  toMode: 'edit' | 'readonly',
): void {
  if (!viewSync) return
  const from = viewSync[viewSyncKey(fileId, fromMode)]
  if (from) {
    viewSync[viewSyncKey(fileId, toMode)] = { ...from }
  }
}
